/* eslint-disable react-hooks/exhaustive-deps */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HyperPayService } from '../../../../../../services/hyperPayService';
import { Section } from '../../../../../../shared/components/common/section';
import { ButtonWrapper } from '../../../../../../shared/components/formUtilities/useButton';
import { AppRoutes } from '../../../../../../constants/appRoutes';
import { buildLocalizeRoute } from '../../../../../../helpers/languageHelper';
import {LanguageService} from '../../../../../../services/languageService';
import { Observables } from '../../../../../../observables';

export const PaymentResult = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const hyperPayService = new HyperPayService();
	const languageService = new LanguageService();

	const [loading, setLoading] = useState(true);
	const [paymentStatus, setPaymentStatus] = useState(null);
	const [error, setError] = useState(null);

	const language = languageService.getCurrentLanguage();

	const resourcePath = searchParams.get("resourcePath");
	const checkoutId = searchParams.get("id");

	// --- Keys used to prevent duplicate verifications ---
	const verificationKey = `payment_verified_${checkoutId}`;
	const verifyingKey = `payment_verifying_${checkoutId}`;
	const resultKey = `payment_result_${checkoutId}`;

	const hasVerified = useRef(sessionStorage.getItem(verificationKey) === "true");
	const isVerifying = useRef(sessionStorage.getItem(verifyingKey) === "true");

	/** ----------------------------------------
	 *  MAIN VERIFICATION EFFECT
	 * ----------------------------------------*/
	useEffect(() => {
		if (!resourcePath) {
			setError(t("invalidPaymentResponse"));
			setLoading(false);
			return;
		}

		// 1) If result already cached → load instantly
		const cachedResult = sessionStorage.getItem(resultKey);
		if (cachedResult) {
			const cachedStatus = JSON.parse(cachedResult);
			setPaymentStatus(cachedStatus);
			// Trigger refresh of available funds if payment was successful
			if (cachedStatus.success) {
				Observables.investorSummaryRefresh.next(1);
			}
			setLoading(false);
			return;
		}

		// 2) If verification already completed → do nothing
		if (hasVerified.current) {
			setLoading(false);
			return;
		}

		// 3) If verification is in progress → wait (short polling)
		if (isVerifying.current) {
			const interval = setInterval(() => {
				const result = sessionStorage.getItem(resultKey);
				if (result) {
					setPaymentStatus(JSON.parse(result));
					sessionStorage.removeItem(verifyingKey);
					clearInterval(interval);
					setLoading(false);
				}
			}, 400);

			// Timeout after 20 seconds
			setTimeout(() => {
				clearInterval(interval);
				setError(t("verificationTimeout"));
				setLoading(false);
				sessionStorage.removeItem(verifyingKey);
			}, 20000);

			return () => clearInterval(interval);
		}

		// 4) Start fresh verification
		isVerifying.current = true;
		sessionStorage.setItem(verifyingKey, "true");

		verifyPayment();
	}, [resourcePath]); // only when redirected from HyperPay

	/** ----------------------------------------
	 *  VERIFY PAYMENT (idempotent)
	 * ----------------------------------------*/
	const verifyPayment = async () => {
		if (hasVerified.current) return;

		try {
			const res = await hyperPayService.getPaymentStatus({
				resourcePath,
				checkoutId,
				language,
			});

			if (!res?.data) throw new Error("Invalid server response");

			let finalStatus = formatPaymentResult(res.data, t);
			setPaymentStatus(finalStatus);

			// Cache results
			sessionStorage.setItem(verificationKey, "true");
			sessionStorage.setItem(resultKey, JSON.stringify(finalStatus));
			sessionStorage.removeItem(verifyingKey);

			hasVerified.current = true;

			// Trigger refresh of available funds in navbar if payment was successful
			if (finalStatus.success) {
				Observables.investorSummaryRefresh.next(1);
			}
		} catch (err) {
			console.error("Payment verification error:", err);

			// prevent retry as HyperPay endpoints can't be called twice reliably
			sessionStorage.setItem(verificationKey, "true");
			sessionStorage.removeItem(verifyingKey);

			setError(
				err?.response?.data?.message || t("verificationFailed")
			);
		} finally {
			isVerifying.current = false;
			setLoading(false);
		}
	};

	/** ----------------------------------------
	 *  Normalize / format payment response
	 * ----------------------------------------*/
	const formatPaymentResult = (status, t) => {
		if (status.isSuccess) {
			return { success: true, ...status };
		}

		return {
			success: false,
			errorMessage: status.resultDescription || t("paymentFailed"),
			...status,
		};
	};

	/** ----------------------------------------
	 *  HANDLERS
	 * ----------------------------------------*/
	const resetSession = () => {
		sessionStorage.removeItem(verificationKey);
		sessionStorage.removeItem(verifyingKey);
		sessionStorage.removeItem(resultKey);
	};

	const handleReturnHome = () => {
		resetSession();
		navigate(buildLocalizeRoute(AppRoutes.Investor));
	};

	const handleRetryPayment = () => {
		resetSession();
		navigate(
			buildLocalizeRoute(
				AppRoutes.Investor + "/" + AppRoutes.ITransfer + "/" + AppRoutes.PaymentCheckout
			)
		);
	};

	/** ----------------------------------------
	 *  RENDER
	 * ----------------------------------------*/
	if (loading) {
		return (
			<Box className="container mx-auto px-4 py-8">
				<Section>
					<Stack spacing={3} alignItems="center" className="py-12">
						<CircularProgress size={60} />
						<Typography variant="h5">
							{t("verifyingPayment")}
						</Typography>
						<Typography color="textSecondary">
							{t("pleaseWait")}
						</Typography>
					</Stack>
				</Section>
			</Box>
		);
	}

	return (
		<Box className="container mx-auto px-4 py-8">
			<Section>
				<Stack spacing={4} alignItems="center" className="py-8">
					{/* Success State */}
					{paymentStatus?.success && (
						<>
							<CheckCircleOutlineIcon
								style={{ fontSize: 80, color: '#4caf50' }}
							/>
							<Typography variant="h4" className="text-center text-green-600">
								{t('paymentSuccessful')}
							</Typography>
							<Typography variant="body1" className="text-center">
								{t('paymentSuccessMessage')}
							</Typography>

							{/* Payment Details */}
							<Box className="w-full max-w-md bg-gray-50 rounded-lg p-6">
								<Stack spacing={2}>
									<Box className="flex justify-between">
										<Typography color="textSecondary">
											{t('transactionId')}:
										</Typography>
										<Typography fontWeight="bold" sx={{ whiteSpace: 'nowrap', ml: 2, fontSize: '13px' }}>
											{paymentStatus.id}
										</Typography>
									</Box>
									<Box className="flex justify-between">
										<Typography color="textSecondary">
											{t('amount')}:
										</Typography>
										<Typography fontWeight="bold">
											{paymentStatus.amount} {paymentStatus.currency}
										</Typography>
									</Box>
									<Box className="flex justify-between">
										<Typography color="textSecondary">
											{t('paymentMethod')}:
										</Typography>
										<Typography fontWeight="bold">
											{paymentStatus.paymentBrand}
										</Typography>
									</Box>
									{paymentStatus.card?.last4Digits && (
										<Box className="flex justify-between">
											<Typography color="textSecondary">
												{t('cardNumber')}:
											</Typography>
											<Typography fontWeight="bold">
												**** **** **** {paymentStatus.card.last4Digits}
											</Typography>
										</Box>
									)}
							</Stack>
						</Box>

						<ButtonWrapper onClick={handleReturnHome} label={t('returnToDashboard')} className="px-8 py-3"/>
					</>
				)}

					{/* Error State */}
					{!paymentStatus?.success && (
						<>
							<ErrorOutlineIcon style={{ fontSize: 80, color: '#f44336' }} />
							<Typography variant="h4" className="text-center text-red-600">
							{t('paymentFailed')}
							</Typography>
							<Typography variant="body1" className="text-center max-w-lg">
								{error ||
									t('paymentFailedMessage') ||
									'Your payment could not be processed. Please try again.'}
							</Typography>

							{paymentStatus?.resultCode && (
								<Box className="w-full max-w-md bg-gray-50 rounded-lg p-6">
									<Stack spacing={2}>
										<Box className="flex justify-between">
											<Typography color="textSecondary">
											{t('errorCode')}:
										</Typography>
										<Typography fontWeight="bold">
											{paymentStatus.resultCode}
										</Typography>
									</Box>
									<Box className="flex justify-between">
										<Typography color="textSecondary">
											{t('description')}:
											</Typography>
											<Typography sx={{ whiteSpace: 'nowrap', ml: 2, fontSize: '13px' }}>
												{paymentStatus.resultDescription}
											</Typography>
										</Box>
									</Stack>
								</Box>
							)}

							<Stack direction="row" spacing={2}>
							<ButtonWrapper label={t('tryAgain')} onClick={handleRetryPayment} className="px-8 py-3">
								</ButtonWrapper>
								<ButtonWrapper
									onClick={handleReturnHome}
									variant="outlined"
									className="px-8 py-3"
								label={t('returnToDashboard')}
								>
								</ButtonWrapper>
							</Stack>
						</>
					)}
				</Stack>
			</Section>
		</Box>
	);
};

